/**
 * @lodex-pro/web-shared/auth/password-rules — one definition of "a valid new password".
 *
 * Quick 260827-pwd. These rules previously lived inline in
 * `forced-password-change.tsx`. Three more flows now need them (voluntary change,
 * forgot-password confirm, and any future provisioning UI), and three copies of a
 * regex list is how they drift apart until two screens disagree about what a valid
 * password is.
 *
 * KNOWN DIVERGENCE, deliberate: the Cognito pool policy is
 * `MinimumLength: 12, RequireUppercase, RequireLowercase, RequireNumbers,
 * RequireSymbols: FALSE`. This schema additionally requires a symbol, so the client
 * is STRICTER than the server. That is the behaviour already shipped and tested in
 * forced-password-change, so it is preserved rather than quietly relaxed here — a
 * password this rejects would be accepted by Cognito, which is safe in that
 * direction. If the two are ever reconciled, change the pool policy first, not this.
 */

import { z } from 'zod';

/** Human-readable summary, for rendering next to the field. */
export const PASSWORD_RULES_TEXT =
  'At least 12 characters, including an uppercase letter, a lowercase letter, a number and a special character.';

/** The single source of truth for new-password complexity. */
export const passwordComplexity = z
  .string()
  .min(12, 'Password must be at least 12 characters.')
  .regex(/[A-Z]/, 'Password must include an uppercase letter.')
  .regex(/[a-z]/, 'Password must include a lowercase letter.')
  .regex(/[0-9]/, 'Password must include a number.')
  .regex(/[^A-Za-z0-9]/, 'Password must include a special character.');

/**
 * new + confirm, with the match check. Shared by every flow that sets a password.
 * Field names are fixed so the components can register them uniformly.
 */
export const newPasswordPairSchema = z
  .object({
    newPassword: passwordComplexity,
    confirmNewPassword: z.string(),
  })
  .refine((v) => v.newPassword === v.confirmNewPassword, {
    path: ['confirmNewPassword'],
    message: 'Passwords must match.',
  });

export type NewPasswordPair = z.infer<typeof newPasswordPairSchema>;
